/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { Drawer } from "@mui/material";
import "./DeviceDrawer";
import ConfirmDialog from "../Shared/Dialogs/ConfirmDialog";
import { setBasicDialogOpenFor, setConfirmDialog, setOpenToast } from "../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../store/hooks";
import { DIALOGOPENFOR } from "../../globals/globals";
import { useSelector } from "react-redux";
import { basicDialogSelector, confirmDialogSelector } from "../../store/helpers/helperSelectors";
import CustomDataGrid from "../Shared/Table/CustomDataGrid";
import { GridEventListener, GridRowParams } from "@mui/x-data-grid";
import { useTaskLogsDisplay } from "../../pages/Tasks/hooks";
import { prepareTasksLogsCellDataGrid } from "../../pages/Configuration/helpers";
import { useDeleteTaskMutation, useExecuteTaskMutation } from "../../store/api/tasks/taskApiSlice";
import { ITask, ITaskLogData, ITaskPropertyKey } from "../../store/api/tasks/types";
import { activeTaskStepsSelector, selectedTaskSelector } from "../../store/api/tasks/selector";
import { setActiveTaskStep, setIsEditTask, setScheduledTask } from "../../store/api/tasks/reducer";
import { customTasks, defaultTasks, taskKeyProps } from "../../globals/constants";
import { IOption } from "../Shared/Filter/types";
import { IKeyValueAny } from "../Administrations/types";
import { useTaskDisplayData } from "../Tasks/hooks";
import TaskFailureDialog from "../../pages/Tasks/TaskFailureDialog";
import { selectProfilePermissionsSelector } from "../../store/api/auth/selectors";
import ProfilePermissions from "../../utils/ProfilePermissions";
import dayjs from "dayjs";
import { getAllSiteGroupList } from "../helpers";
import { capitalizeWords, errorMessage } from "../../utils/helpers";
import { currentSiteGroupsSelector } from "../../store/api/admin/selectors";
import { setSelectedTaskType } from "../../store/api/admin/reducers";
import { selectedGeoRegionsSelector } from "../../store/api/admin/selectors";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Icon } from "../Shared/Icon";
import { Trash2, Play, Clock, CircleCheck, CircleX, Pause, CircleDot, Loader2 } from "lucide-react";
import StatusIcon from "../Shared/Status/StatusIcon";
import { AppState } from "../../store";
import { IGeoRegion } from "../../store/api/admin/types";

interface ITaskDrawerProps {
  data?: ITask;
  closedButtonClicked: (arg: boolean) => void;
  handleRefeshTasks: () => void;
  setRowClicked: (arg: boolean) => void;
}

const TaskDrawer: React.FC<ITaskDrawerProps> = ({ data, handleRefeshTasks, closedButtonClicked, setRowClicked }) => {
  const [isOpen, setOpen] = React.useState(true);
  const [action, setAction] = React.useState("");
  const [taskKey, setTaskKey] = React.useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = React.useState<string | undefined>(undefined);
  const dispatch = useStoreDispatch();
  const { taskLogData = [], isLoading, taskLogsColumns } = useTaskLogsDisplay(data?.name, isOpen);
  const [exucetueTask] = useExecuteTaskMutation();
  const [removeTask] = useDeleteTaskMutation();

  // const navigate = useNavigate();

  const args = { taskName: data?.name };
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const selectedTask = useSelector(selectedTaskSelector);
  const { deviceList } = useTaskDisplayData();
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const currentSiteGroups = useSelector(currentSiteGroupsSelector);
  const selectedGeoRegions = useSelector<AppState, IGeoRegion[]>(selectedGeoRegionsSelector) || [];
  const groupList = getAllSiteGroupList(currentSiteGroups) || [];
  // const [getTaskLogs] = useGetTaskLogsQuery();
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const activeTaskStep = useSelector(activeTaskStepsSelector) || 0;

  const rowsData = prepareTasksLogsCellDataGrid(taskLogData) || [];

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === "keydown" && ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")) {
      return;
    }

    setOpen(open);
    closedButtonClicked(open);
  };

  const summaryFields = [
    { key: "Type", value: data?.taskName || "—" },
    { key: "Status", value: data?.status || "—" },
    { key: "Repeat", value: data?.period || "—" },
  ];

  const handleClickAction = async (actionName: string) => {
    if (actionName?.match(action.toLowerCase())) {
      if (actionName?.match(DIALOGOPENFOR.DELETETASK?.toLowerCase())) {
        handleDeleteTask();
      } else if (actionName?.match(DIALOGOPENFOR.RUNTASK?.toLowerCase())) {
        handleExcuteTask();
      }
    } else {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: `${action} action canceled!` }));
    }
  };
  const handleExcuteTask = async () => {
    try {
      const response: any = await exucetueTask(args);
      if (response?.error) throw response.error;
      if (response?.data?.status === 204) {
        dispatch(setOpenToast({ openToast: true, toastType: "success", message: `${action} action has initiated!` }));
      }
    } catch (err) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(err) }));
    }
  };

  const handleDeleteTask = async () => {
    try {
      const response: any = await removeTask(args);
      if (response?.error) throw response.error;
      if (response?.data?.status === 200) {
        handleRefeshTasks();
        closedButtonClicked(false);
        dispatch(setOpenToast({ openToast: true, toastType: "success", message: `${action} action has initiated!` }));
      }
    } catch (err) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(err) }));
    }
  };

  const taskActionHandler = (args: string) => {
    if (args?.toLowerCase() === DIALOGOPENFOR.DELETETASK.toLowerCase()) {
      dispatch(setConfirmDialog(DIALOGOPENFOR.DELETETASK));
      setAction(args);
    } else if (args?.toLowerCase() === DIALOGOPENFOR.RUNTASK.toLowerCase()) {
      dispatch(setConfirmDialog(DIALOGOPENFOR.RUNTASK));
      setAction(args);
    } else return;
  };
  const cellTypes = ["lteCellEnable", "lteCellDisable", "nrCellEnable", "nrCellDisable"];

  const taskEditHandler = (args: string) => {
    if (Object.keys(selectedTask).length > 0) {
      let devices: IOption[] = [];
      let siteGroups: IOption[] = [];
      let regions: IOption[] = [];
      let taskProps: IKeyValueAny = {};
      let executionTargetNames = "";
      let selectedTaskType: string = "";
      let selectedTaskName: string = "";
      if (selectedTask.taskProperty.length > 0) {
        if (cellTypes.includes(selectedTask.taskType)) {
          const taskTypeStringArray = selectedTask.taskType.split(/(?=[A-Z])/); //split by capital letter
          taskProps = { ...taskProps, cellAction: taskTypeStringArray[2] };
          selectedTaskType = selectedTask.taskType.replace(taskTypeStringArray[2], "");
          selectedTaskName = customTasks[selectedTaskType];
          // const enable = "Enable";
          // const disable = "Disable";
          // if (selectedTask.taskType.includes(enable)) {
          //   taskProps = { ...taskProps, cellAction: enable };
          //   selectedTaskType = selectedTask.taskType.replace(enable, "");
          // } else {
          //   taskProps = { ...taskProps, cellAction: disable };
          //   selectedTaskType = selectedTask.taskType.replace(disable, "");
          // }
        } else {
          selectedTaskType = selectedTask.taskType;
          selectedTaskName = selectedTask.taskName;
        }
        selectedTask?.taskProperty?.forEach((element: ITaskPropertyKey) => {
          if (element?.property?.includes("DEVICES")) {
            const deviceOption = deviceList.filter((item: IOption) => item.value === element.value)[0];
            !!deviceOption && devices.push(deviceOption);
            taskProps = { ...taskProps, executionTarget: "", executionTargetName: "" };
          }
          let selectedTaskProperties = { ...taskKeyProps[selectedTaskType], ...taskKeyProps.taskProperties };
          if (element?.property?.includes("EXECUTION_TARGET")) {
            selectedTaskProperties = { ...taskKeyProps[selectedTaskType], ...taskKeyProps.taskProperties, ...taskKeyProps?.taskExecutionType };
          }
          if (element?.property?.includes("EXECUTION_TARGET_NAME")) {
            executionTargetNames = element?.value || "";
          }
          Object.entries(selectedTaskProperties)?.forEach(([key, value]) => {
            if (value === element?.property) {
              if (element.value === "true" || element.value === "false") {
                taskProps = { ...taskProps, [key]: element.value === "true" };
              } else {
                taskProps = { ...taskProps, [key]: element.value };
              }
            }
          });
        });

    const target = (taskProps.executionTarget || "").toUpperCase();
    if (executionTargetNames && target === "GROUP") {
      const arr = executionTargetNames.split(",").map((v: string) => v.trim()).filter(Boolean);
      arr.forEach((grp: string) => {
        const groupOption = groupList?.filter((item: IOption) => item.value === grp)[0];
        if (groupOption) {
          siteGroups.push(groupOption);
        }
      });
    }
    if (executionTargetNames && target === "REGION") {
      const arr = executionTargetNames.split(",").map((v: string) => v.trim()).filter(Boolean);
      arr.forEach((regionId: string) => {
        const region = selectedGeoRegions.find((item: IGeoRegion) => item.objectKey === regionId);
        if (region) {
          regions.push({ title: region.name || regionId, value: region.objectKey || regionId });
        } else {
          regions.push({ title: regionId, value: regionId });
        }
      });
    }
      }

      dispatch(
        setScheduledTask({
          ...taskProps,
          customReportInputMode:
            selectedTaskType === "customAuditReport"
              ? (taskProps.templateName ? "TEMPLATE" : taskProps.manualAttributes ? "MANUAL" : "TEMPLATE")
              : taskProps.customReportInputMode,
          taskName: { title: selectedTaskName, value: selectedTaskType },
          name: selectedTask.name,
          description: selectedTask.description,
          scheduleOnce: selectedTask.scheduleOnce,
          nextTime: selectedTask.nextTime > 0 ? selectedTask.nextTime : dayjs().add(1, "minute").valueOf(),
          period: selectedTask.period,
          className: selectedTask.className,
          deviceName: devices,
          siteGroupList: siteGroups,
          regionList: regions,
        })
      );
      dispatch(setSelectedTaskType((taskProps.executionTarget || "DEVICE").toUpperCase()));
      if (defaultTasks?.includes(selectedTask.name)) {
        if (["Database Backup", "Database Integrity"]?.includes(selectedTask?.name)) {
          dispatch(setActiveTaskStep(activeTaskStep + 2));
        } else dispatch(setActiveTaskStep(activeTaskStep + 3));
      }
      dispatch(setIsEditTask(true));
      dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ADDSCHEDULEDTASK));
      setRowClicked(false);
    } else {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: "Oops! Something went wrong." }));
    }
  };

  const handleRowClicked: GridEventListener<"rowClick"> = (params: GridRowParams<ITaskLogData>) => {
    if (params?.row?.taskExecutionKey) {
      setTaskKey(params?.row?.taskExecutionKey);
      setSelectedStatusFilter(undefined);
      dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.TASKFAILDIALOG));
    }
  };

    const handleStatusCountClick = (row: any, status: string) => (event: React.MouseEvent) => {
    event.stopPropagation();
    if (row?.taskExecutionKey && row[status.toLowerCase()] > 0) {
      setTaskKey(row.taskExecutionKey);
      setSelectedStatusFilter(status);
      dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.TASKFAILDIALOG));
    }
  };

  // Create custom columns with clickable status counts
  const customTaskLogsColumns = React.useMemo(() => {
    return taskLogsColumns.map((col: any) => {
      if (col.field === "status") {
        return {
          ...col,
          renderCell: (cellValues: any) => {
            const rawStatus = cellValues.value == null ? "" : String(cellValues.value);
            const normalizedStatus = rawStatus ? `${rawStatus.charAt(0).toUpperCase()}${rawStatus.slice(1).toLowerCase()}` : "";
            return <StatusIcon status={normalizedStatus} />;
          },
        };
      }

      const statusFields = ['total', 'success', 'failed', 'idle', 'running'];
      
      if (statusFields.includes(col.field)) {
        return {
          ...col,
          renderCell: (cellValues: any) => {
            const count = cellValues.value || 0;
            const status = col.field === 'total' ? 'Total' : col.headerName;
            const isClickable = count > 0;
            
            return (
              <span
                onClick={isClickable ? handleStatusCountClick(cellValues.row, status) : undefined}
                className={`${isClickable ? 'cursor-pointer text-primary font-semibold hover:text-primary/80' : 'text-foreground'}`}
              >
                {count}
              </span>
            );
          },
        };
      }
      return col;
    });
  }, [taskLogsColumns]);

  const isDefaultTask = defaultTasks.includes(data?.taskType || "");



  return (
    <>
      {(openConfirmDialog === DIALOGOPENFOR.DELETETASK || openConfirmDialog === DIALOGOPENFOR.RUNTASK) && (
        <ConfirmDialog actionName={`Yes, ${action}`} handleClickAction={handleClickAction}>
          Are you sure you want to {action} <strong>{data?.name || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      {basicDialogOpenFor === DIALOGOPENFOR.TASKFAILDIALOG && <TaskFailureDialog taskName={data?.name || ""} taskExecutionKey={taskKey} initialStatus={selectedStatusFilter} />}
      <Drawer
        PaperProps={{
          sx: {
            width: "560px",
            maxWidth: "90vw",
            height: "100%",
            top: 0,
            backgroundColor: "var(--color-background)",
            borderLeft: "1px solid var(--color-border)",
            borderRadius: "10px 0 0 10px",
            boxShadow: "-4px 0 24px 0 rgba(0,0,0,0.08)",
            overflow: "hidden",
          },
        }}
        ModalProps={{ disableEnforceFocus: true }}
        anchor="right"
        open={isOpen}
        onClose={toggleDrawer(false)}
      >
        <div className="flex flex-col h-full min-h-0 bg-background">
          {/* ─── Header ─── */}
          <div className="shrink-0 px-4 pt-2 pb-2">
            <div className="flex items-start justify-between gap-2">
              {/* Task name */}
              <h2 className="text-xl font-semibold truncate text-foreground" title={data?.name || ""}>
                {data?.name || "Unknown"}
              </h2>

              {/* Close button */}
              <button
                type="button"
                className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-md bg-transparent hover:bg-transparent text-foreground border-0 p-0 focus-visible:outline-none"
                aria-label="Close"
                onClick={toggleDrawer(false) as any}
              >
                <Icon name="close" size={20} />
              </button>
            </div>

            {/* Status badge (like DeviceDrawer's SN / Maintenance) */}
            <div className="flex items-center gap-2 mt-1 flex-wrap pb-1">
              <Badge variant="secondary" className="font-normal">
                {data?.taskName || "Task"}
              </Badge>
              <span className="text-sm inline-flex items-center gap-1.5">
                <StatusIcon status={capitalizeWords(data?.status || "Unknown")} />
                {/* <span className="truncate text-muted-foreground">{data?.status || "Unknown"}</span> */}
              </span>
            </div>

            {/* Action buttons: Edit / Run now on left, Delete on right */}
            <div className="flex w-full items-center gap-2 pt-3 pb-2">
              {permissions?.hasOpCodeGroup("task_management") && !!data?.taskType && !isDefaultTask && (
                <Button variant="outline" size="default" className="h-8" onClick={() => taskEditHandler("Edit")}>
                  Edit
                </Button>
              )}
              {permissions?.hasOpCodeGroup("manage_sn") && (
                <Button
                  variant="outline"
                  type="button"
                  className="border border-input bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
                  onClick={() => taskActionHandler("Run task")}
                >
                  Run now
                </Button>
              )}
              {permissions?.hasOpCodeGroup("task_management") && !!data?.taskType && !isDefaultTask && (
                <div className="ml-auto flex justify-end items-end">
                  <Button
                    variant="outline"
                    size="default"
                    className="h-8 text-destructive hover:text-destructive"
                    onClick={() => taskActionHandler("Delete task")}
                    disabled={defaultTasks?.includes(selectedTask?.name)}
                  >
                    <Trash2 />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* ─── Scrollable content ─── */}
          <div className="flex-1 min-h-0 overflow-auto px-4 pb-4 space-y-4">
            {/* ─── Summary Card ─── */}
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
                  {summaryFields.map((item, index) => (
                    <div className="flex flex-col gap-1" key={index}>
                      <span className="text-muted-foreground">{item.key}</span>
                      <span className="font-medium truncate" title={String(item.value)}>
                        {capitalizeWords(item.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            {/* ─── Task Logs ─── */}
            <Card>
              <CardContent className="pt-3 space-y-4">
                <h4 className="text-sm font-semibold text-foreground">Task Logs</h4>
                <CustomDataGrid
                  tableId="taskLogInDrawer"
                  sx={{ maxHeight: "400px" }}
                  rows={rowsData}
                  columns={customTaskLogsColumns}
                  onRowClick={handleRowClicked}
                  loading={isLoading}
                  paginationMode="client"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default TaskDrawer;
